/**
 * Woodside's WordPress embeds write one shared `data-params` convention across
 * every widget on the site: a comma-separated list of `Key=Value` pairs with
 * an optional `@` prefix.
 *
 *   data-params="campus=troy"
 *   data-params="@CongregationID=15,@DaysOfWeek=Monday,Tuesday"
 *
 * Query-string style (`&`) is accepted too. Keys are handed to the finder's
 * own parser (`src/lib/filters.ts`), which knows both the short names and the
 * old CustomWidget's `@Name` forms.
 *
 * Note: comma is both the pair separator and the list separator in the old
 * convention. A pair without `=` is treated as a continuation of the previous
 * value, so `@DaysOfWeek=Monday,Tuesday` still parses as one list.
 */
export function parseDataParams(raw: string | null | undefined): [string, string][] {
  if (!raw) return [];
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* already plain text */
  }
  const out: [string, string][] = [];
  for (const piece of decoded.split(/[,&]/)) {
    const eq = piece.indexOf('=');
    if (eq === -1) {
      const last = out[out.length - 1];
      if (last && piece.trim()) last[1] = `${last[1]},${piece.trim()}`;
      continue;
    }
    const key = piece.slice(0, eq).trim();
    const value = piece.slice(eq + 1).trim();
    if (!key || !value) continue; // `campus=` means "unset", not "match empty"
    out.push([key, value]);
  }
  return out;
}

/** Treat a missing or blank data attribute as unset. */
export function attr(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
